import { css } from '@emotion/react'
import api from 'api'
import { Button, Checkbox, HFlow, Icon, isEmpty, Tag, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useSession } from 'components/auth/useSession'
import { isLotacaoOrEstagio } from 'components/auth/useSessionUtils'
import { HLabel } from 'components/HLabel'
import { useFlags } from 'config/useFlagsContext'
import { useInativarAtendimentoMutation } from 'graphql/hooks.generated'
import { OrigemDadoEnum, TipoAtendimentoProfissional, TipoRegistroHistoricoClinico } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import React, { Fragment, useMemo, useState } from 'react'
import { AccordionItemButton } from 'react-accessible-accordion'
import { condicaoMaternalRecord } from 'types/enums'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { stringGrouper } from 'util/string/string-grouper'
import { convertCiap, convertCid10, isCodigoNaoInformado } from 'view/atendimentos/detail/utils-cuidadoCompartilhado'
import {
  LabelCondutaCuidadoCompartilhadoDwRecord,
  LabelEvolucaoCuidadoCompartilhadoRecord,
} from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'
import { AtendimentoInativoActions } from 'view/retificacao-atendimento/AtendimentoInativoActions'
import { RetificacaoAtendimentoButton } from 'view/retificacao-atendimento/RetificacaoAtendimentoButton'
import { RetificacaoAtendimentoModal } from 'view/retificacao-atendimento/RetificacaoAtendimentoModal'
import { RetificacaoAtendimentoStatus } from 'view/retificacao-atendimento/RetificacaoAtendimentoStatus'
import {
  isAtendimentoProfissionalVacinacao,
  mostrarAcoesAtendimentoInativo,
  permitirExcluirAtendimento,
  permitirExibirBotaoRetificacao,
} from 'view/retificacao-atendimento/util-retificacaoAtendimento'

import { useHistoricoPrint } from '../../../hooks/useHistoricoPrint'
import {
  CidadaoHistorico,
  HistoricoAtendimentoModel,
  SubtipoAtendimentoTag,
  tipoRegistroHistoricoData,
} from '../../../model/model-historico'
import {
  ATENDIMENTO_NAO_PERMITE_IMPRESSAO,
  getImpressaoHistoricoTooltip,
  getTooltipExcluirVacinaText,
  hasSomeSubmodulesPlano,
  horaOrTurnoByOrigem,
  isFichaAtendimento,
  isHistoricoPrintEnable,
} from '../../../utils/utils-historico'
import HistoricoHeaderEllipsis from '../../HistoricoHeaderEllipsis'
import { useImpressaoEscutaInicialHistorico } from '../detail/escuta-inicial/ImpressaoEscutaInicialHistorico'
import CiapCidLabel from './components/CiapCidLabel'
import ProfissionaisHistoricoHeaderLabel from './components/ProfissionaisHistoricoHeaderLabel'
import { VisualizarDiscussaoCuidadoCompartilhadoAction } from './components/VisualizarDiscussaoCuidadoCompartilhadoAction'
import RemoveAtendimentoConfirmModal from './RemoveAtendimentoConfirmModal'
import RemoveAtendimentoErrorModal from './RemoveAtendimentoErrorModal'
import HistoricoAccordionHeaderTitleTags from './title/HistoricoAccordionHeaderTitleTags'
import HistoricoAccordionHeaderTitleText from './title/HistoricoAccordionHeaderTitleText'
import { HistoricoDadosClinicos } from './title/HistoricoDadosClinicos'

export interface HistoricoAccordionHeaderProps {
  historicoAtendimento: HistoricoAtendimentoModel
  subtipoAtendimento: SubtipoAtendimentoTag
  refetchOnListagem?(): void
  cidadao: CidadaoHistorico
  checked?: boolean
  onSelectToPrint?(checked: boolean, historico: HistoricoAtendimentoModel): void
  historicoPrintProps: ReturnType<typeof useHistoricoPrint>
  isAccordionDisabled: boolean
}

export default function HistoricoAccordionHeader(props: Readonly<HistoricoAccordionHeaderProps>) {
  const {
    historicoAtendimento,
    subtipoAtendimento,
    cidadao,
    refetchOnListagem,
    checked,
    onSelectToPrint,
    historicoPrintProps,
    isAccordionDisabled,
  } = props

  const [inativarAtendimento, { loading }] = useInativarAtendimentoMutation()
  const { analytics } = useFirebase()
  const alert = useAlert()
  const theme = useTheme()
  const flags = useFlags()
  const { downloadEscutaInicial } = useImpressaoEscutaInicialHistorico(historicoAtendimento.idAtendRecente)
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  const [modalRetificacaoAtendimentoOpen, setModalRetificacaoAtendimentoOpen] = useState(false)
  const [modalRemoveOpen, setModalRemoveOpen] = useState(false)
  const [modalRemoveErrorOpen, setModalRemoveErrorOpen] = useState(false)

  const isTipoAtendimentoProfissionalVacinacao = isAtendimentoProfissionalVacinacao(historicoAtendimento.tipoAtendProf)

  const isAtendimentoVacinacao =
    isTipoAtendimentoProfissionalVacinacao ||
    historicoAtendimento.tipoApresentacao === TipoRegistroHistoricoClinico.VACINACAO

  const isEscutaInicial =
    historicoAtendimento.tipoAtendProf === TipoAtendimentoProfissional.ESCUTA_INICIAL ||
    historicoAtendimento.tipoApresentacao === TipoRegistroHistoricoClinico.ESCUTA_INICIAL

  const isCuidadoCompartilhado =
    historicoAtendimento.tipoApresentacao === TipoRegistroHistoricoClinico.CUIDADO_COMPARTILHADO

  const { data, checkAuthorization } = useSession()

  const acesso = data?.acesso
  const profissional = data?.profissional

  const hasPermissaoRegistrarAtendimento = checkAuthorization(
    Permissions.visualizarListaDeAtendimento.registrarAtendimento
  )

  const hasPermissaoRegistrarAtendimentoVacinacao = checkAuthorization(
    Permissions.visualizarListaDeAtendimento.registrarAtendimento.registrarAtendimentoDeVacinacao
  )

  const isAcessoLotacaoCriadora =
    acesso && profissional
      ? historicoAtendimento.profissional?.id === profissional.id &&
        isLotacaoOrEstagio(acesso) &&
        historicoAtendimento.cbo?.id === acesso.cbo.id &&
        historicoAtendimento.unidadeSaude?.id === acesso.unidadeSaude.id &&
        historicoAtendimento.equipe?.id === acesso.equipe?.id
      : false

  const podeExcluirAtendimento = permitirExcluirAtendimento(
    isAcessoLotacaoCriadora,
    historicoAtendimento.idAtendRecente,
    !!historicoAtendimento.isCancelado
  )

  const showRetificacaoAtendimentoButton = permitirExibirBotaoRetificacao(
    historicoAtendimento.tipoAtendProf,
    !!historicoAtendimento.isCancelado,
    hasPermissaoRegistrarAtendimento,
    hasPermissaoRegistrarAtendimentoVacinacao,
    flags
  )

  const isInativoOrRetificado = historicoAtendimento.isInativo || historicoAtendimento.isRetificado

  const isShowAtendimentoInativoActions = mostrarAcoesAtendimentoInativo(
    historicoAtendimento.tipoAtendProf,
    isInativoOrRetificado,
    flags
  )

  const hasCondicoesAvaliadas =
    (historicoAtendimento.tipoApresentacao === TipoRegistroHistoricoClinico.ATENDIMENTO_DOMICILIAR ||
      historicoAtendimento.tipoApresentacao === TipoRegistroHistoricoClinico.AVALIACAO_ELEGIBILIDADE) &&
    !isEmpty(historicoAtendimento.condicoesAvaliadas)
  const hasExamesRealizados = !isEmpty(historicoAtendimento.examesRealizadosZika)
  const horaOuTurnoAtendimento = horaOrTurnoByOrigem(historicoAtendimento)

  const tipoAtendimentoData =
    tipoRegistroHistoricoData[(historicoAtendimento.tipoApresentacao ?? historicoAtendimento.tipoAtendProf).toString()]

  const isAtendimentoPrintEnable = isHistoricoPrintEnable(historicoAtendimento)
  const isFicha = isFichaAtendimento(historicoAtendimento.origemAtendimento)

  const isPrintEscutaInicialDisabled =
    !historicoAtendimento.idAtendRecente || historicoAtendimento.origemAtendimento !== OrigemDadoEnum.PEC

  const isPrintObservacaoDisabled =
    historicoAtendimento.hasObservacao && !isFicha && !historicoAtendimento.isAtendObsFinalizado

  const printTooltipText = getImpressaoHistoricoTooltip(
    isAtendimentoPrintEnable,
    isPrintEscutaInicialDisabled,
    isPrintObservacaoDisabled,
    historicoAtendimento.isInativo,
    isEscutaInicial
  )

  const classes = useMemo(() => createStyles(theme, tipoAtendimentoData.barColor, isAccordionDisabled), [
    isAccordionDisabled,
    theme,
    tipoAtendimentoData.barColor,
  ])

  const printHistorico = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    const fileName = isAtendimentoVacinacao ? `AtendimentoVacinacao.pdf` : `AtendimentoIndividual.pdf`
    const impressaoPath = isAtendimentoVacinacao
      ? api.atendimentoVacinacao.imprimirAtendimentoVacinacao
      : api.atendimentoIndividual.imprimirAtendimentoIndividual
    isEscutaInicial
      ? downloadEscutaInicial()
      : handleImpressaoPath({
          apiPath: impressaoPath,
          values: {
            atendimentoProfissionalId: historicoAtendimento.idAtendRecente,
            historicoCabecalhoId: historicoAtendimento.idAtendProcessado,
          },
          file: fileName,
          dateFormat: reportFormatDate,
        }).catch(handleImpressaoError)

    analytics.logEvent('click_button_historico_impressao_atendimento')
  }

  const handleChangePrintCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectToPrint(e.target.checked, historicoAtendimento)
  }

  const handleOpenRemoveConfirmModal = () => setModalRemoveOpen(true)

  const handleOpenRemoveConfirmButtonClick = (e: Event) => {
    e.stopPropagation()
    handleOpenRemoveConfirmModal()
  }

  const handleOpenRemoveConfirmButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation()
      handleOpenRemoveConfirmModal()
    }
  }

  const handleRemoveAtendimentoSuccess = () => {
    refetchOnListagem()
    historicoPrintProps.remove(historicoAtendimento)
  }

  const handleOnRemoveAtendimento = () => {
    inativarAtendimento({
      variables: {
        input: {
          atendimentoProfissionalId: historicoAtendimento.idAtendRecente,
          historicoCabecalhoId: historicoAtendimento.idAtendProcessado,
        },
      },
    })
      .then(() => {
        setModalRemoveOpen(false)
        alert(
          'success',
          'Atendimento de vacinação excluído com sucesso. As alterações estarão disponíveis na RNDS em torno de 15 dias após o envio para o Centralizador Nacional'
        )
        handleRemoveAtendimentoSuccess()
      })
      .catch(() => {
        setModalRemoveOpen(false)
        setModalRemoveErrorOpen(true)
      })
  }

  const handleRetificacaoAtendimentoModalOpen = () => setModalRetificacaoAtendimentoOpen(true)

  const handleRetificacaoAtendimentoModalClose = () => setModalRetificacaoAtendimentoOpen(false)

  const hasCondutaCuidadoCompartilhado =
    !isCodigoNaoInformado(historicoAtendimento.cuidadoCompartilhadoEvolucao?.conduta) ||
    !isUndefinedOrNull(historicoAtendimento.conduta)
  const hasReclassificacaoPrioridade =
    !isCodigoNaoInformado(historicoAtendimento.reclassificacaoPrioridade) ||
    !isUndefinedOrNull(historicoAtendimento.cuidadoCompartilhadoEvolucao?.reclassificacaoPrioridade)

  return (
    <Fragment>
      <RemoveAtendimentoConfirmModal
        modalRemoveOpen={modalRemoveOpen}
        setModalRemoveOpen={setModalRemoveOpen}
        handleRemoveAtendimento={handleOnRemoveAtendimento}
        loading={loading}
        cidadao={cidadao}
        historicoAtendimento={historicoAtendimento}
      />
      <RemoveAtendimentoErrorModal
        modalRemoveErrorOpen={modalRemoveErrorOpen}
        setModalRemoveErrorOpen={setModalRemoveErrorOpen}
      />
      <RetificacaoAtendimentoModal
        cidadaoId={cidadao.id}
        dataAtendimento={historicoAtendimento.dataAtendimento}
        atendProfId={historicoAtendimento.idAtendRecente}
        historicoCabecalhoId={historicoAtendimento.idAtendProcessado}
        open={modalRetificacaoAtendimentoOpen}
        onClose={handleRetificacaoAtendimentoModalClose}
        nomeCidadao={cidadao.nomeSocial ?? cidadao.nome}
        tipoAtendimentoProfissional={historicoAtendimento.tipoAtendProf}
        onRemoveSuccess={handleRemoveAtendimentoSuccess}
        descricaoRegistrosRemovidos={historicoAtendimento.dadosClinicos}
      />
      <AccordionItemButton css={classes.accordionButton}>
        <div css={classes.barColor} />
        <div css={classes.accordionButtonContent}>
          <HFlow>
            <div
              css={css`
                padding-top: 0.25rem;
              `}
            >
              <Tooltip text={printTooltipText}>
                <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  {/* TODO (Foundation): Remover condição de disabled para escuta */}
                  <Checkbox
                    checked={checked}
                    onChange={handleChangePrintCheckbox}
                    disabled={!isAtendimentoPrintEnable || isEscutaInicial}
                  />
                </div>
              </Tooltip>
            </div>
            <VFlow vSpacing={0.5}>
              <HFlow
                style={css`
                  display: inline-flex;
                  flex-wrap: wrap;
                `}
                alignItems='center'
                hSpacing={0.5}
              >
                <HistoricoAccordionHeaderTitleText
                  tipoAtendimentoData={tipoAtendimentoData}
                  horaOuTurnoAtendimento={horaOuTurnoAtendimento}
                />
                <HistoricoAccordionHeaderTitleTags
                  historicoAtendimento={historicoAtendimento}
                  subtipoAtendimento={subtipoAtendimento}
                />
              </HFlow>
              <ProfissionaisHistoricoHeaderLabel
                historicoAtendimento={historicoAtendimento}
                isCuidadoCompartilhado={isCuidadoCompartilhado}
              />
              {isCuidadoCompartilhado && (
                <CiapCidLabel
                  ciap={convertCiap(historicoAtendimento)}
                  cid10={convertCid10(historicoAtendimento)}
                  isBold
                />
              )}
              {!!historicoAtendimento?.estagiario && (
                <Text>
                  Estagiário:
                  {historicoAtendimento.estagiario.nomeSocial
                    ? ` ${historicoAtendimento.estagiario.nomeSocial} (Nome social) `
                    : ` ${historicoAtendimento.estagiario.nome} `}
                  - {historicoAtendimento.cboEstagio.nome.capitalize()} - {historicoAtendimento.cboEstagio.cbo2002}
                  {historicoAtendimento.estagiario.nomeSocial &&
                    ` | Nome civil - ${historicoAtendimento.estagiario.nomeCivil}`}
                </Text>
              )}

              {hasCondicoesAvaliadas && (
                <HistoricoHeaderEllipsis tooltipText='Mais condições avaliadas'>
                  <HLabel title='Condições avaliadas:'>
                    {historicoAtendimento?.condicoesAvaliadas &&
                      stringGrouper(...historicoAtendimento.condicoesAvaliadas)}
                  </HLabel>
                </HistoricoHeaderEllipsis>
              )}
              {historicoAtendimento.dadosClinicos && (
                <HistoricoDadosClinicos dadosClinicos={historicoAtendimento.dadosClinicos} />
              )}
              {hasSomeSubmodulesPlano(historicoAtendimento) && (
                <HFlow style={classes.planoTagsWrapper} hSpacing={0.5}>
                  {historicoAtendimento.hasIvcf && <Tag type='normal'>IVCF-20</Tag>}
                  {historicoAtendimento.hasPrescricaoMedicamento && <Tag type='normal'>Prescrição de medicamento</Tag>}
                  {historicoAtendimento.hasAlergia && <Tag type='normal'>Alergia</Tag>}
                  {historicoAtendimento.hasAtestado && <Tag type='normal'>Atestado</Tag>}
                  {historicoAtendimento.hasOrientacao && <Tag type='normal'>Orientação</Tag>}
                  {historicoAtendimento.hasSolicitacaoExame && (
                    <Tag type='normal'>Solicitação de exame/procedimento</Tag>
                  )}
                  {historicoAtendimento.hasResultadoExame && <Tag type='normal'>Resultado de exame</Tag>}
                  {(historicoAtendimento.hasEncaminhamentoEspecializado ||
                    historicoAtendimento.hasEncaminhamento ||
                    historicoAtendimento.hasGuiasEncaminhamentos) && <Tag type='normal'>Encaminhamento</Tag>}
                  {historicoAtendimento.hasProcedimentoClinico && <Tag type='normal'>Procedimento clínico</Tag>}
                  {historicoAtendimento.hasMarcadorConsumoAlimentar && (
                    <Tag type='normal'>Marcadores de consumo alimentar</Tag>
                  )}
                  {historicoAtendimento.hasCuidadoCompartilhado && <Tag type='normal'>Compartilhamento de cuidado</Tag>}
                  {historicoAtendimento.hasMchat && <Tag type='normal'>M-CHAT-R/F</Tag>}
                </HFlow>
              )}
              {isAtendimentoVacinacao && (
                <HFlow hSpacing={0.5}>
                  {historicoAtendimento.condicoesVacinacao?.condicaoMaternal && (
                    <Tag type='normal'>
                      {condicaoMaternalRecord[historicoAtendimento.condicoesVacinacao?.condicaoMaternal]?.tagLabel}
                    </Tag>
                  )}
                  {historicoAtendimento.condicoesVacinacao?.isViajante && <Tag type='normal'>Viajante</Tag>}
                  {historicoAtendimento.condicoesVacinacao?.isComunicanteHanseniase && (
                    <Tag type='normal'>Comunicante de hanseníase</Tag>
                  )}
                </HFlow>
              )}
              {isCuidadoCompartilhado && (hasCondutaCuidadoCompartilhado || hasReclassificacaoPrioridade) && (
                <HFlow hSpacing={0.5}>
                  {hasCondutaCuidadoCompartilhado && (
                    <Tag type='normal'>
                      {
                        LabelEvolucaoCuidadoCompartilhadoRecord[
                          historicoAtendimento.cuidadoCompartilhadoEvolucao?.conduta ??
                            LabelCondutaCuidadoCompartilhadoDwRecord[historicoAtendimento.conduta]
                        ]
                      }
                    </Tag>
                  )}
                  {hasReclassificacaoPrioridade && <Tag type='normal'>Reclassificação de prioridade</Tag>}
                </HFlow>
              )}
              {historicoAtendimento.hasAnexoArquivo && <Tag type='normal'>Anexo de arquivos</Tag>}
              {hasExamesRealizados && (
                <HistoricoHeaderEllipsis tooltipText='Mais exames avaliados'>
                  <HLabel title='Exames realizados:'>
                    {historicoAtendimento?.examesRealizadosZika &&
                      stringGrouper(...historicoAtendimento.examesRealizadosZika)}
                  </HLabel>
                </HistoricoHeaderEllipsis>
              )}
            </VFlow>
          </HFlow>
          <HFlow style={classes.options} hSpacing={0.5} alignItems='center'>
            {/* TODO (RNG: 29771) Remover marcado para exclusao */}
            {historicoAtendimento.isCancelado && (
              <div css={classes.marcadoParaExclusao}>
                <Icon
                  icon='clockOutline'
                  size={1}
                  style={css`
                    margin-right: 0.5rem;
                  `}
                />
                Marcado para exclusão
              </div>
            )}
            {isAtendimentoVacinacao && !flags.RETIFICACAO_ATENDIMENTO_VACINACAO_ENABLED && (
              <span>
                <Tooltip
                  text={getTooltipExcluirVacinaText(
                    podeExcluirAtendimento,
                    historicoAtendimento.origemAtendimento,
                    historicoAtendimento.isCancelado
                  )}
                >
                  <Button
                    size='small'
                    skin='ghost'
                    onClick={(e) => handleOpenRemoveConfirmButtonClick(e)}
                    onKeyDown={handleOpenRemoveConfirmButtonKeyDown}
                    disabled={!podeExcluirAtendimento}
                  >
                    <Icon icon='trashOutline' size={1.5} />
                  </Button>
                </Tooltip>
              </span>
            )}
            {isEscutaInicial && (
              <span>
                <Tooltip text={isPrintEscutaInicialDisabled ? ATENDIMENTO_NAO_PERMITE_IMPRESSAO : 'Imprimir'}>
                  <Button size='small' skin='ghost' disabled={isPrintEscutaInicialDisabled} onClick={printHistorico}>
                    <Icon icon='printerOutline' size={1.5} />
                  </Button>
                </Tooltip>
              </span>
            )}
            {isInativoOrRetificado && (
              <RetificacaoAtendimentoStatus
                status={historicoAtendimento.isInativo ? 'Removido' : 'Editado'}
                data={
                  historicoAtendimento.isInativo
                    ? historicoAtendimento.dataInativacao
                    : historicoAtendimento.dataUltimaRetificacao
                }
              />
            )}
            {showRetificacaoAtendimentoButton && (
              <RetificacaoAtendimentoButton
                historicoAtendimento={historicoAtendimento}
                onClick={handleRetificacaoAtendimentoModalOpen}
              />
            )}
            {isShowAtendimentoInativoActions && (
              <AtendimentoInativoActions coUnicoAtendProf={historicoAtendimento.codigoUnicoRegistro} />
            )}
            {isCuidadoCompartilhado && (
              <VisualizarDiscussaoCuidadoCompartilhadoAction
                cuidadoCompartilhadoId={historicoAtendimento.cuidadoCompartilhado?.id}
                viaHistorico
              />
            )}
            <Tooltip text={historicoAtendimento.isInativo && accordionDisabledTooltipText}>
              <Icon name='accordionAngleIcon' icon='angleDown' size={1.5} />
            </Tooltip>
          </HFlow>
        </div>
      </AccordionItemButton>
    </Fragment>
  )
}

const accordionDisabledTooltipText = 'Só é possível visualizar detalhes sobre registros removidos por meio da impressão'

const createStyles = (theme: Theme, barColor: string, isAccordionDisabled: boolean) => {
  return {
    accordionButton: css`
      display: flex;
      cursor: ${isAccordionDisabled ? 'not-allowed' : 'pointer'};
      border-radius: 2px;
      border: 0px;
      &:focus {
        outline: none;
        box-shadow: none;
      }
      &:hover {
        background-color: ${theme.pallete.gray.c90};
      }
      &[aria-expanded='true'] div:first-of-type {
        border-bottom-left-radius: 2.5px;
      }
      &[aria-expanded='true'] {
        border-bottom: 1px solid ${theme.pallete.gray.c80};
      }
      &[aria-expanded='false'] {
        border-bottom: 0px;
      }
      &[aria-expanded='true'] svg[name='accordionAngleIcon'],
      &[aria-selected='true'] svg[name='accordionAngleIcon'] {
        transform: rotate(180deg);
        transition: 0.4s;
      }
      &[aria-expanded='false'] svg[name='accordionAngleIcon'] {
        ${isAccordionDisabled ? `color: ${theme.pallete.gray.c70};` : ''}
        transform: rotate(0deg);
        transition: 0.4s;
      }
    `,
    marcadoParaExclusao: css`
      color: ${theme.pallete.status.danger.c50};
      display: flex;
    `,
    options: css`
      height: 2.5rem;
    `,
    barColor: css`
      width: 0.5rem;
      background-color: ${barColor};
      border-top-left-radius: 1px;
      border-bottom-left-radius: 1px;
    `,
    accordionButtonContent: css`
      justify-content: space-between;
      background-color: ${theme.pallete.surface.background};
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
      padding: 0.5rem;
      display: flex;
      width: 100%;
    `,
    planoTagsWrapper: css`
      display: flex;
      flex-wrap: wrap;
    `,
  }
}
